/* Port of Han card-hover.js; only Shopify selector/lifecycle adapters differ. */
(() => {
  'use strict';
  if (window.__hanCardMotion) return;
  window.__hanCardMotion = true;
  const fine = matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  // Restore native carousel measurements after each hover; app controls stay native.
  const groups = [
    ['.han-editorial:not(.han-editorial--new) .han-editorial-grid', '.han-editorial-card'],
    ['.han-discovery .collections-mosaic', '.collections-tile:not(.collections-tile--straw)'],
    ['.han-story .bd-pair', 'img'],
    ['.han-story-source .about-photo-pair', 'figure'],

  ];
  const sessions = new Map();
  const enabled = () => document.documentElement.dataset.cardMotion !== 'false' && fine.matches && !reduced.matches;
  function locate(target) {
    if (!(target instanceof Element)) return null;
    for (const [groupSelector, itemSelector] of groups) {
      const card = target.closest(itemSelector);
      if (card?.parentElement?.matches(groupSelector) && (!card.closest('.swiper,.swiper-wrapper') || card.closest('body[data-han-page="index"] .popular-products'))) return {group: card.parentElement, card, itemSelector};
    }
    return null;
  }
  function remember(session, element, property, value, priority = '') {
    let saved = session.styles.get(element);
    if (!saved) session.styles.set(element, saved = new Map());
    if (!saved.has(property)) saved.set(property, [element.style.getPropertyValue(property), element.style.getPropertyPriority(property)]);
    element.style.setProperty(property, value, priority);
  }
  function mark(session, element, name, value) {
    let saved = session.attributes.get(element);
    if (!saved) session.attributes.set(element, saved = new Map());
    if (!saved.has(name)) saved.set(name, element.getAttribute(name));
    element.setAttribute(name, value);
  }
  function cleanup(session) {
    clearTimeout(session.timer);
    session.observer?.disconnect();
    session.styles.forEach((properties, element) => properties.forEach(([value, priority], name) => {
      if (value) element.style.setProperty(name, value, priority);
      else element.style.removeProperty(name);
    }));
    session.attributes.forEach((attributes, element) => attributes.forEach((value, name) => {
      if (value === null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    }));
    sessions.delete(session.group);
    session.group.closest('.swiper')?.swiper?.update();
  }
  function resetAll() { [...sessions.values()].forEach(cleanup); }
  function targets(session, active, gain) {
    const row = session.rows.find(items => items.some(item => item.card === active));
    const result = new Map(session.items.map(item => [item.card, {width: item.width, x: 0}]));
    if (!row || row.length < 2) return result;
    const selected = row.find(item => item.card === active);
    const peers = row.reduce((sum, item) => sum + (item === selected ? 0 : item.width), 0);
    let delta = 0;
    row.forEach(item => {
      const width = item.width + (item === selected ? gain : -gain * item.width / peers);
      result.set(item.card, {width, x: session.flex ? 0 : delta});
      delta += width - item.width;
    });
    return result;
  }
  function write(session, values) {
    session.items.forEach(item => {
      const value = values.get(item.card);
      remember(session, item.card, '--elastic-width', `${value.width}px`);
      remember(session, item.card, '--elastic-x', `${value.x}px`);
    });
  }
  function create({group, itemSelector}) {
    const items = [...group.children].filter(card => card.matches(itemSelector)).map(card => {
      const rect = card.getBoundingClientRect();
      return {card, width: rect.width, height: rect.height, left: rect.left, top: card.offsetTop};
    }).filter(item => item.width > 1 && item.height > 1 && !item.card.hidden && getComputedStyle(item.card).display !== 'none');
    const rows = [];
    items.forEach(item => {
      let row = rows.find(row => Math.abs(row[0].top - item.top) < 3);
      if (!row) rows.push(row = []);
      row.push(item);
    });
    rows.forEach(row => row.sort((a, b) => a.left - b.left));
    if (!rows.some(row => row.length > 1)) return null;
    const style = getComputedStyle(group);
    const session = {group, items, rows, flex: style.display.includes('flex'), styles: new Map(), attributes: new Map(), timer: 0};
    sessions.set(group, session);
    mark(session, group, 'data-elastic-group', session.flex ? 'flex' : 'grid');
    if (session.flex) remember(session, group, 'scroll-snap-type', 'none', 'important');
    else if (style.gridTemplateColumns && style.gridTemplateColumns !== 'none') remember(session, group, 'grid-template-columns', style.gridTemplateColumns);
    items.forEach(item => {
      mark(session, item.card, 'data-elastic-item', '');
      remember(session, item.card, '--elastic-height', `${item.height}px`);
      remember(session, item.card, '--elastic-width', `${item.width}px`);
      remember(session, item.card, '--elastic-x', '0px');
      item.card.querySelectorAll('.collection-product-visual').forEach(visual => {
        const height = visual.getBoundingClientRect().height;
        mark(session, visual, 'data-elastic-visual', '');
        remember(session, visual, '--elastic-visual-height', `${height}px`);
      });
    });
    if (typeof MutationObserver !== 'undefined') {
      session.observer = new MutationObserver(() => cleanup(session));
      session.observer.observe(group, {childList: true});
    }
    return session;
  }
  function activate(found) {
    let session = sessions.get(found.group);
    if (!session) session = create(found);
    if (!session) return;
    if (session.active === found.card && !session.timer) return;
    clearTimeout(session.timer); session.timer = 0;
    const row = session.rows.find(row => row.some(item => item.card === found.card));
    if (!row || row.length < 2) { release(session); return; }
    const selected = row.find(item => item.card === found.card);
    const peers = row.reduce((sum, item) => sum + (item === selected ? 0 : item.width), 0);
    const gain = Math.min(selected.width * .14, peers * .08);
    // Measure text fit without painting a jump, then animate from the current interpolated geometry.
    const current = new Map(session.items.map(item => [item.card, {
      width: item.card.getBoundingClientRect().width,
      x: session.flex ? 0 : parseFloat(getComputedStyle(item.card).translate) || 0
    }]));
    const transitions = session.items.map(item => [item.card, item.card.style.getPropertyValue('transition'), item.card.style.getPropertyPriority('transition')]);
    session.items.forEach(item => item.card.style.setProperty('transition', 'none', 'important'));
    let destination = targets(session, found.card, 0);
    for (const amount of [gain, gain * .5, gain * .25, 0]) {
      destination = targets(session, found.card, amount);
      write(session, destination);
      if (!amount || row.every(item => item.card.scrollHeight <= item.card.clientHeight + 2 && item.card.scrollWidth <= item.card.clientWidth + 2)) break;
    }
    write(session, current);
    void found.group.offsetWidth;
    transitions.forEach(([card, value, priority]) => value ? card.style.setProperty('transition', value, priority) : card.style.removeProperty('transition'));
    void found.group.offsetWidth;
    session.active = found.card;
    write(session, destination);
  }
  function release(session) {
    if (!session || session.timer) return;
    session.active = null;
    write(session, targets(session, null, 0));
    session.timer = setTimeout(() => cleanup(session), 650);
  }
  document.addEventListener('pointerover', event => {
    if (event.pointerType === 'touch' || !enabled()) return;
    const found = locate(event.target);
    if (found) activate(found);
  });
  document.addEventListener('pointerout', event => {
    const from = locate(event.target);
    if (!from || from.card.contains(event.relatedTarget)) return;
    const to = locate(event.relatedTarget);
    if (to?.group === from.group) return;
    release(sessions.get(from.group));
  });
  document.addEventListener('pointercancel', resetAll);
  document.addEventListener('pointerdown', event => {
    if (event.pointerType === 'touch' || event.target.closest?.('[data-slide],.popular-products__btn-next,.popular-products__btn-prev')) resetAll();
  }, {capture: true, passive: true});
  document.addEventListener('click', event => { if (event.target.closest?.('[data-slide],.popular-products__btn-next,.popular-products__btn-prev')) resetAll(); }, true);
  window.addEventListener('resize', resetAll, {passive: true});
  window.addEventListener('scroll', resetAll, {capture: true, passive: true});
  window.addEventListener('pagehide', resetAll);
  document.addEventListener('visibilitychange', () => { if (document.hidden) resetAll(); });
  ['sttoke:type-change', 'sttoke:appearance-change', 'sttoke:cart-open', 'sttoke:search-open', 'variant:change', 'variant-change', 'shopify:section:load', 'shopify:section:unload'].forEach(name => document.addEventListener(name, resetAll));
  document.addEventListener('change', event => { if (event.target.matches('input[name=\"id\"],select[name=\"id\"]') || event.target.closest('variant-radios,variant-selects')) resetAll(); });
  // Clean detached rows after section/variant replacement without reacting to sweep overlays.
  new MutationObserver(() => { [...sessions.values()].forEach(session => { if (!session.group.isConnected) cleanup(session); }); }).observe(document.body, {childList:true,subtree:true});
  fine.addEventListener('change', resetAll);
  reduced.addEventListener('change', resetAll);
})();

/* Linked colour previews: delegated so app-inserted and filtered cards work too. */
(() => {
  'use strict';
  if (window.__hanColourPreview) return;
  window.__hanColourPreview = true;
  const selector = '.card-wrapper .pl-swatches-collection__link[href]';
  const images = new Map();
  const active = new Map();
  function imageFor(link) {
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) return Promise.resolve(null);
    const key = url.pathname + url.search;
    if (!images.has(key)) {
      const request = fetch(url.pathname.replace(/\/$/, '') + '.js', {credentials: 'same-origin'})
        .then(response => { if (!response.ok) throw new Error('Product unavailable'); return response.json(); })
        .then(product => {
          const variant = product.variants?.find(item => String(item.id) === url.searchParams.get('variant'));
          const source = variant?.featured_image?.src || product.featured_image;
          if (!source) return null;
          const imageUrl = new URL(source, location.href);
          imageUrl.searchParams.set('width', '900');
          const image = new Image();
          image.src = imageUrl.href;
          return image.decode().then(() => imageUrl.href);
        }).catch(() => { images.delete(key); return null; });
      images.set(key, request);
    }
    return images.get(key);
  }
  function clear(card) {
    const state = active.get(card);
    if (!state) return;
    state.overlay?.remove();
    state.originals?.forEach(([image, value, priority]) => {
      if (value) image.style.setProperty('visibility', value, priority);
      else image.style.removeProperty('visibility');
    });
    active.delete(card);
  }
  async function preview(link) {
    const card = link.closest('.card-wrapper');
    const original = card?.querySelector('.card__inner .media img.media--first');
    if (!original || active.get(card)?.link === link) return;
    clear(card);
    const state = {link};
    active.set(card, state);
    const src = await imageFor(link);
    if (!src || active.get(card) !== state || !link.isConnected) return;
    const overlay = original.cloneNode(false);
    overlay.removeAttribute('id');
    overlay.removeAttribute('srcset');
    overlay.removeAttribute('sizes');
    overlay.removeAttribute('loading');
    overlay.src = src;
    overlay.alt = '';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.className = 'han-colour-preview';
    for (const [property, value] of Object.entries({position:'absolute',inset:'0',width:'100%',height:'100%',opacity:'1',visibility:'visible',transform:'none',transition:'none','z-index':'2','pointer-events':'none','object-fit':getComputedStyle(original).objectFit})) {
      overlay.style.setProperty(property, value, 'important');
    }
    // Product PNGs may be transparent; hide both native image layers, including
    // the theme's secondary hover image, rather than stacking visible products.
    state.originals = [...original.parentElement.querySelectorAll('img')].map(image => [
      image, image.style.getPropertyValue('visibility'), image.style.getPropertyPriority('visibility')
    ]);
    state.originals.forEach(([image]) => image.style.setProperty('visibility', 'hidden', 'important'));
    state.overlay = overlay;
    original.parentElement.append(overlay);
  }
  const swatch = target => target instanceof Element ? target.closest(selector) : null;
  document.addEventListener('pointerover', event => {
    if (event.pointerType === 'touch' || !matchMedia('(hover: hover)').matches) return;
    const link = swatch(event.target);
    if (link) preview(link);
  });
  document.addEventListener('pointerout', event => {
    const link = swatch(event.target);
    if (link && !link.contains(event.relatedTarget) && document.activeElement !== link) clear(link.closest('.card-wrapper'));
  });
  document.addEventListener('focusin', event => { const link = swatch(event.target); if (link) preview(link); });
  document.addEventListener('focusout', event => { const link = swatch(event.target); if (link) clear(link.closest('.card-wrapper')); });
  const reset = () => [...active.keys()].forEach(clear);
  document.addEventListener('shopify:section:unload', reset);
  document.addEventListener('pointercancel', reset);
  window.addEventListener('pagehide', reset);
})();
